import { useState, useEffect, useRef } from "react";

const ReferralButton = ({ user }) => {
  const [showReferralTooltip, setShowReferralTooltip] = useState(false);
  const [showReferralMenu, setShowReferralMenu] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef(null);

  const referralBonus = process.env.NEXT_PUBLIC_REFERRAL_BONUS_POINTS || '500';
  const referralEarnings = 0

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowReferralMenu(false);
      }
    };

    if (showReferralMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReferralMenu]);

  useEffect(() => {
    if (copyMessage) {
      const timer = setTimeout(() => {
        setCopyMessage('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copyMessage]);

  const handleCopyReferralCode = () => {
    if (!user?.referralCode) return;
    
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopyMessage('Referral code copied!');
      setTimeout(() => setShowReferralMenu(false), 1500);
    }).catch(err => {
      console.error('Copy failed:', err);
      setCopyMessage('Failed to copy');
    });
  };

  const handleCopyReferralLink = () => {
    if (!user?.referralCode) return;
    
    const referralUrl = `${window.location.origin}?ref=${user.referralCode}`;
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopyMessage('Referral link copied!');
      setTimeout(() => setShowReferralMenu(false), 1500);
    }).catch(err => {
      console.error('Copy failed:', err);
      setCopyMessage('Failed to copy');
    });
  };

  const handleShareReferral = () => {
    if (!user?.referralCode) return;

    const referralUrl = `${window.location.origin}?ref=${user.referralCode}`;
    const shareText = `Hey! 👋

      I've been using this awesome platform and thought you might like it too!

      Sign up with my referral code: ${user.referralCode}

      Link: ${referralUrl}`;

    if (navigator.share) {
      navigator.share({
        title: 'Join with my referral code',
        text: shareText,
      }).then(() => {
        setShowReferralMenu(false);
      }).catch(err => console.log('Share cancelled'));
    } else {
      handleCopyReferralLink();
    }
  };

  if (!user?.referralCode || isMobile) return null;

  return (
    <li 
      ref={menuRef}
      className="account-access rbt-user-wrapper right-align-dropdown"
      onMouseEnter={() => setShowReferralTooltip(true)}
      onMouseLeave={() => setShowReferralTooltip(false)}
      style={{ position: 'relative' }}
    >
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => setShowReferralMenu(!showReferralMenu)}
          style={{ 
            fontSize: '14px',
            padding: '6px 14px',
            whiteSpace: 'nowrap',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            height: '32px',
            lineHeight: '1'
          }}
        >
          <i className="feather-gift" style={{ marginRight: '6px' }}></i>
          Refer & Earn
        </button>
        
        {referralEarnings > 0 && (
          <span className="rbt-notification-count" style={{
            position: 'absolute',
            top: '-12px',
            right: '-12px',
            fontSize: '10px',
            fontWeight: '700',
            padding: '4px 6px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(74, 222, 128, 0.4)',
            border: '2px solid #fff',
            minWidth: '32px',
            height: '20px',
            textAlign: 'center',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            whiteSpace: 'nowrap'
          }}>
            <span style={{ fontSize: '9px', lineHeight: '1' }}>💰</span>
            <span style={{ lineHeight: '1' }}>
              {referralEarnings > 9999 ? `${(referralEarnings / 1000).toFixed(1)}k` : referralEarnings}
            </span>
          </span>
        )}
      </div>
      
      {showReferralTooltip && !showReferralMenu && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '10px',
            padding: '16px 18px',
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            zIndex: 1000,
            minWidth: '260px',
            maxWidth: '300px',
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <span style={{ 
              fontSize: '15px', 
              fontWeight: '600', 
              color: '#1f2937',
              display: 'block',
              marginBottom: '6px'
            }}>
              🎁 Earn Rewards Together!
            </span>
            <span style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              lineHeight: '1.6',
              display: 'block'
            }}>
              Share the love and earn <strong style={{ color: '#2563eb' }}>{referralBonus} points</strong> for every friend who joins. They get rewarded too!
            </span>
          </div>
        </div>
      )}

      {showReferralMenu && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '10px',
            padding: '8px 0',
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
            zIndex: 1000,
            minWidth: '240px',
          }}
        >
          <div style={{ 
            padding: '16px 18px', 
            borderBottom: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
          }}>
            <strong style={{ 
              display: 'block', 
              marginBottom: '6px', 
              color: '#1f2937', 
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Your Referral Code
            </strong>
            <span style={{ 
              fontSize: '18px', 
              color: '#2563eb', 
              fontWeight: 'bold', 
              letterSpacing: '1.5px',
              fontFamily: 'monospace'
            }}>
              {user.referralCode}
            </span>
          </div>

          {copyMessage && (
            <div style={{
              padding: '12px 18px',
              backgroundColor: '#dcfce7',
              color: '#166534',
              fontSize: '13px',
              fontWeight: '500',
              textAlign: 'center',
              borderBottom: '1px solid #e5e7eb'
            }}>
              ✓ {copyMessage}
            </div>
          )}
          
          <button
            onClick={handleCopyReferralCode}
            style={{
              width: '100%',
              padding: '14px 18px',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'background-color 0.2s',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <i className="feather-copy" style={{ fontSize: '18px', color: '#2563eb' }}></i>
            Copy Referral Code
          </button>

          <button
            onClick={handleCopyReferralLink}
            style={{
              width: '100%',
              padding: '14px 18px',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'background-color 0.2s',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <i className="feather-link" style={{ fontSize: '18px', color: '#2563eb' }}></i>
            Copy Referral Link
          </button>

          <button
            onClick={handleShareReferral}
            style={{
              width: '100%',
              padding: '14px 18px',
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'background-color 0.2s',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <i className="feather-share-2" style={{ fontSize: '18px', color: '#2563eb' }}></i>
            Share on Social Media
          </button>
        </div>
      )}
    </li>
  );
};

export default ReferralButton;